import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { BaseTextFieldProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import DiffFee from "work/proposals/redlining/__diff/view/diff-fee";
import { FeeType } from "work/proposals/redlining/fee-schedule-redline/fee-redline/fee-redline";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";

const RedlineTextBox = styled(TextField)(() => ({
  minWidth: "8rem",
  "& .MuiTypography-root": {
    height: "unset",
  },
}));
const TextInputButtons = styled("div")(({ theme }) => ({
  alignSelf: "flex-end",
  display: "flex",
  marginBottom: theme.spacing(1.5),
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

export interface FeeRedlineInputProps extends BaseTextFieldProps {
  categoryRedline: FeeScheduleCategoryRedline;
  showTextInput?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  onFeeChange?: (newFee: Fee) => void;
  onShowTextInput?: () => void;
  onHideTextInput?: () => void;
}

export default function FeeRedlineInput(props: Readonly<FeeRedlineInputProps>) {
  const {
    categoryRedline,
    focused,
    readOnly,
    onFeeChange,
    onShowTextInput,
    onHideTextInput,
    ...otherProps
  } = props;

  const getEditableFee = React.useCallback((): FeeType | undefined => {
    if (categoryRedline?.feeRedline?.currentEntry !== undefined) return categoryRedline.feeRedline.currentEntry;
    if (categoryRedline?.feeRedline?.revisedEntry !== false) return categoryRedline?.feeRedline?.revisedEntry;
    return categoryRedline?.feeRedline?.originalEntry;
  }, [categoryRedline]);

  const [showTextInput, setShowTextInput] = React.useState<boolean>(
    props.showTextInput ?? false
  );
  const [currentFee, setCurrentFee] = React.useState<FeeType | undefined>(
    getEditableFee()
  );

  const isCurrentFeeValid = React.useMemo(() => {
    if (!(currentFee instanceof Fee)) return false;
    return currentFee.unit === null || currentFee.unit.trim().length > 0;
  }, [currentFee]);

  const hasChanged = React.useMemo(() => {
    const editableFee = getEditableFee();
    if (editableFee instanceof Fee && currentFee instanceof Fee) {
      return !editableFee.isEqualTo(currentFee);
    }
    return editableFee !== currentFee;
  }, [currentFee, getEditableFee]);

  function handleSubmitEdit() {
    if (!(currentFee instanceof Fee)) return;
    setShowTextInput(false);
    onFeeChange?.(currentFee);
    onHideTextInput?.();
  }

  function handleShowTextInput() {
    setCurrentFee(getEditableFee());
    setShowTextInput(true);
    onShowTextInput?.();
  }

  function handleHideTextInput() {
    setCurrentFee(getEditableFee());
    setShowTextInput(false);
    onHideTextInput?.();
  }

  function renderTextInputButtons() {
    if (readOnly) return null;
    return (
      <TextInputButtons>
        <Tooltip title="Save Change">
          <span>
            <ActionButton
              color="primary"
              disabled={!hasChanged || !isCurrentFeeValid}
              onClick={handleSubmitEdit}
            >
              <CheckIcon />
            </ActionButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <span>
            <ActionButton color="inherit" onClick={handleHideTextInput}>
              <CloseIcon />
            </ActionButton>
          </span>
        </Tooltip>
      </TextInputButtons>
    );
  }

  function renderActionButtons() {
    if (readOnly) return null;
    return (
      <>
        {!showTextInput && !readOnly && (
          <Tooltip title="Edit">
            <span>
              <ActionButton onClick={handleShowTextInput}>
                <EditNoteIcon color="primary" />
              </ActionButton>
            </span>
          </Tooltip>
        )}
        {showTextInput && renderTextInputButtons()}
      </>
    );
  }

  return (
    <RedlineTextBox
      {...otherProps}
      focused={focused}
      variant={props.variant ?? "outlined"}
      margin={props.margin ?? "normal"}
      slotProps={{
        htmlInput: {
          isNewlyAdded: categoryRedline.currentEntry && !categoryRedline.revisedEntry,
          feeRedline: categoryRedline.feeRedline,
          showTextInput: showTextInput,
          onFeeInputChange: setCurrentFee,
          onTextInputSubmit: handleSubmitEdit,
          onTextInputCancel: handleHideTextInput,
          onClick: handleShowTextInput,
        },
        input: {
          inputComponent: DiffFee,
          disableUnderline: true,
          endAdornment: renderActionButtons(),
        },
        inputLabel: {
          shrink: categoryRedline.feeRedline !== undefined
        }
      }}
    />
  );
}
